import { Fragment, useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Plus } from "lucide-react";
import BulkTaskActionBar from "@/components/navigation/BulkTaskActionBar/BulkTaskActionBar";
import { ComplexTaskItem, TaskLifecycleIcon } from "@/components/tasks/lists/ComplexTaskItem/ComplexTaskItem";
import CreateTaskSheet from "@/components/tasks/sheets/CreateTaskSheet/CreateTaskSheet";
import DayListHeader, { type SortOption } from "@/components/calendar/DayListHeader/DayListHeader";
import TaskFilterSheet, {
  EMPTY_TASK_FILTER,
  activeTaskFilterCount,
  matchesTaskFilter,
  type TaskFilterState,
} from "@/components/tasks/options/TaskFilterSheet/TaskFilterSheet";
import SimpleTaskItem from "@/components/tasks/lists/SimpleTaskItem/SimpleTaskItem";
import TaskTimelineRow, { TimelineNowSeparator } from "@/components/tasks/lists/TaskTimelineRow/TaskTimelineRow";
import TaskSwipeActions from "@/components/tasks/lists/TaskSwipeActions/TaskSwipeActions";
import TaskStatusDivider, {
  shouldShowStatusDivider,
  statusCount,
  statusRank,
  useCollapsedStatuses,
} from "@/components/tasks/lists/TaskStatusDivider/TaskStatusDivider";
import { Button } from "@/components/ui/button";
import { selectRange, toggleOne } from "@/lib/bulkSelection";
import { DEFAULT_AREA_COLOR, resolveTaskColors } from "@/lib/listColors";
import { formatDateKey, parseDateKey, todayKey } from "@/lib/time";
import { trpc } from "@/lib/trpc";
import {
  advanceTaskExecution,
  deriveTaskLifecycle,
  getExecutionControlState,
  getSecondaryStatusSwipe,
  resetTaskStatusTiming,
} from "@shared/taskLifecycle";
import {
  compareTaskTimeline,
  getTaskTimelineWindow,
  isTaskTimelineActive,
} from "@shared/taskTimeline";

export function monthRange(date: string) {
  const parsed = parseDateKey(date);
  const start = new Date(parsed.getFullYear(), parsed.getMonth(), 1);
  const end = new Date(parsed.getFullYear(), parsed.getMonth() + 1, 0);
  return {
    startDate: formatDateKey(start),
    endDate: formatDateKey(end),
  };
}

export const HOME_DAY_SORT_KEY = "home-day-sort-mode";
export const CALENDAR_DAY_SORT_KEY = "calendar-day-sort-mode";

export function storedDaySortMode(storageKey: string): SortOption {
  if (typeof localStorage === "undefined") return "time";
  const saved = localStorage.getItem(storageKey);
  return saved === "status" || saved === "time" || saved === "list" ? saved : "time";
}

export function useDaySortMode(storageKey: string) {
  const [sortMode, setSortMode] = useState<SortOption>(() => storedDaySortMode(storageKey));

  const handleSortChange = (sort: SortOption) => {
    setSortMode(sort);
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(storageKey, sort);
    }
  };

  return { sortMode, handleSortChange };
}

function sortDayTasks<T extends {
  status: "pending" | "done" | "skipped";
  startedAt: Date | null;
  completedAt: Date | null;
  startMinutes: number | null;
  priority: number;
  listId: number | null;
}>(tasks: T[], sortMode: SortOption) {
  const sorted = [...tasks];
  const timeForSort = (task: T) => {
    const lifecycle = deriveTaskLifecycle(task);
    if (lifecycle === "done" && task.completedAt) return new Date(task.completedAt).getTime();
    if (lifecycle === "doing" && task.startedAt) return new Date(task.startedAt).getTime();
    if (task.startMinutes != null) return task.startMinutes * 60 * 1000;
    return null;
  };
  const compareByTime = (a: T, b: T) => {
    const aTime = timeForSort(a);
    const bTime = timeForSort(b);
    if (aTime == null && bTime == null) return 0;
    if (aTime == null) return 1;
    if (bTime == null) return -1;
    return aTime - bTime;
  };
  if (sortMode === "status") {
    sorted.sort((a, b) => statusRank(a) - statusRank(b) || compareByTime(a, b));
  } else if (sortMode === "time") {
    sorted.sort(compareTaskTimeline);
  } else if (sortMode === "priority") {
    sorted.sort((a, b) => (a.priority ?? 3) - (b.priority ?? 3));
  } else if (sortMode === "list") {
    sorted.sort((a, b) => (a.listId ?? 0) - (b.listId ?? 0) || compareTaskTimeline(a, b));
  }
  return sorted;
}

export function groupTasksByArea<T extends { listId?: number | null; listName?: string | null }>(tasks: T[]) {
  const groups = new Map<string, { id: number | null; name: string; tasks: T[] }>();
  for (const task of tasks) {
    const key = task.listId == null ? "inbox" : String(task.listId);
    const group = groups.get(key) ?? {
      id: task.listId ?? null,
      name: task.listName ?? "Inbox",
      tasks: [],
    };
    group.tasks.push(task);
    groups.set(key, group);
  }
  return Array.from(groups.values());
}

export function SimpleDayTaskList({
  date,
  sortMode,
  onSortChange,
  activeOnly = false,
  showFilters = true,
  onDateClick,
}: {
  date: string;
  sortMode: SortOption;
  onSortChange: (sort: SortOption) => void;
  activeOnly?: boolean;
  showFilters?: boolean;
  onDateClick?: () => void;
}) {
  const [, setLocation] = useLocation();
  const utils = trpc.useUtils();
  const listGroups = useCollapsedStatuses();
  const [collapsedLists, setCollapsedLists] = useState<Set<string>>(() => new Set());
  const [filterOpen, setFilterOpen] = useState(false);
  const [taskFilter, setTaskFilter] = useState<TaskFilterState>(EMPTY_TASK_FILTER);
  const tasksQuery = trpc.day.list.useQuery({ date });
  const unfilteredTasks = useMemo(() => {
    const allTasks = tasksQuery.data ?? [];
    const visibleTasks = activeOnly
      ? allTasks.filter(task => {
          const lifecycle = deriveTaskLifecycle(task);
          return lifecycle === "pending" || lifecycle === "doing";
        })
      : allTasks;
    return sortDayTasks(visibleTasks, sortMode);
  }, [activeOnly, sortMode, tasksQuery.data]);
  const tasks = useMemo(
    () => showFilters ? unfilteredTasks.filter(task => matchesTaskFilter(task, taskFilter)) : unfilteredTasks,
    [showFilters, taskFilter, unfilteredTasks],
  );
  const filterLists = useMemo(() => groupTasksByArea(unfilteredTasks).map(list => ({ id: list.id, name: list.name })), [unfilteredTasks]);
  const markDone = trpc.tasks.markDone.useMutation({
    onSuccess: () => {
      utils.day.list.invalidate();
      utils.day.monthSummary.invalidate();
    },
  });
  const toggleSkipped = trpc.tasks.toggleSkipped.useMutation({
    onSuccess: () => utils.day.list.invalidate(),
  });

  const taskRow = (task: typeof tasks[number]) => (
    <li key={task.id} className="task">
      <TaskSwipeActions
        task={task}
        onSwipeRight={() => markDone.mutate({ id: task.id, clientTimestamp: new Date().toISOString() })}
        onSwipeLeft={() => toggleSkipped.mutate({ id: task.id, clientTimestamp: new Date().toISOString() })}
      >
        <SimpleTaskItem
          task={task}
          dimCompleted={false}
          onClick={() => setLocation(`/task/${task.id}`)}
        />
      </TaskSwipeActions>
    </li>
  );

  const content = tasksQuery.isLoading ? (
      <div className="space-y-1.5">
        {[0, 1, 2].map(i => (
          <div key={i} className="h-5 rounded-md bg-muted/40 animate-pulse" />
        ))}
      </div>
  ) : tasks.length === 0 ? (
    <p className="text-sm text-muted-foreground/80 italic">{activeOnly ? "Nothing active for this day." : "Nothing scheduled."}</p>
  ) : sortMode === "list" ? (
    <div className="calendar-area-groups">
      {groupTasksByArea(tasks).map(group => {
        const key = String(group.id ?? "inbox");
        const collapsed = collapsedLists.has(key);
        return (
          <section key={key} className="calendar-timeline-group calendar-area-group">
            <button
              type="button"
              className="group-header"
              onClick={() => setCollapsedLists(current => {
                const next = new Set(current);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                return next;
              })}
              aria-expanded={!collapsed}
            >
              <span>{group.name}</span>
              <span>{group.tasks.length}</span>
            </button>
            {!collapsed && <ul className="rows">{group.tasks.map(taskRow)}</ul>}
          </section>
        );
      })}
    </div>
  ) : (
    <ul className="task-list divide-y divide-border/50 border border-border/60 rounded-xl overflow-hidden bg-card">
      {tasks.map((task, index) => {
        const lifecycle = deriveTaskLifecycle(task);
        const collapsed = sortMode === "status" && listGroups.collapsed.has(lifecycle);
        return (
          <Fragment key={task.id}>
            {sortMode === "status" && shouldShowStatusDivider(tasks, index) && (
              <li className="status-divider">
                <TaskStatusDivider
                  status={lifecycle}
                  count={statusCount(tasks, lifecycle)}
                  collapsed={collapsed}
                  onToggle={() => listGroups.toggle(lifecycle)}
                />
              </li>
            )}
            {!collapsed && (
              taskRow(task)
            )}
          </Fragment>
        );
      })}
    </ul>
  );

  return (
    <section aria-label="Items for selected day" className="space-y-3">
      <DayListHeader
        date={date}
        today={todayKey()}
        count={tasks.length}
        sortMode={sortMode}
        onSortChange={onSortChange}
        onDateClick={onDateClick}
        {...(showFilters ? {
          filterActiveCount: activeTaskFilterCount(taskFilter),
          onFilterClick: () => setFilterOpen(true),
        } : {})}
      />
      {showFilters && <TaskFilterSheet open={filterOpen} onOpenChange={setFilterOpen} value={taskFilter} onChange={setTaskFilter} lists={filterLists} />}
      {content}
    </section>
  );
}

export function CalendarDayTaskList({
  date,
  label,
  sortMode,
  onSortChange,
}: {
  date: string;
  label: string;
  sortMode: SortOption;
  onSortChange: (sort: SortOption) => void;
}) {
  const [, setLocation] = useLocation();
  const utils = trpc.useUtils();
  const selectedListGroups = useCollapsedStatuses();
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [taskFilter, setTaskFilter] = useState<TaskFilterState>(EMPTY_TASK_FILTER);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [lastSelected, setLastSelected] = useState<{
    scope: "day";
    id: number;
  } | null>(null);
  const [now, setNow] = useState(() => new Date());

  const tasksQuery = trpc.day.list.useQuery({ date });
  const unfilteredTasks = useMemo(() => {
    return sortDayTasks(tasksQuery.data ?? [], sortMode);
  }, [tasksQuery.data, sortMode]);
  const selectedTasks = useMemo(
    () => unfilteredTasks.filter(task => matchesTaskFilter(task, taskFilter)),
    [taskFilter, unfilteredTasks],
  );
  const filterLists = useMemo(
    () => groupTasksByArea(unfilteredTasks).map(list => ({ id: list.id, name: list.name })),
    [unfilteredTasks],
  );
  const handleFilterChange = (next: TaskFilterState) => {
    setTaskFilter(next);
    setSelected(new Set());
    setLastSelected(null);
    setSelectMode(false);
  };
  const timelineGroups = useMemo(() => {
    const scheduled = selectedTasks.filter(task => getTaskTimelineWindow(task) !== null);
    const unscheduled = selectedTasks.filter(task => getTaskTimelineWindow(task) === null);
    return { scheduled, unscheduled };
  }, [selectedTasks]);

  const areaGroups = useMemo(() => groupTasksByArea(selectedTasks), [selectedTasks]);

  const timelineNow = useMemo(() => {
    if (date !== formatDateKey(now) || timelineGroups.scheduled.length === 0) return null;
    const entries = timelineGroups.scheduled.map(task => ({
      task,
      window: getTaskTimelineWindow(task)!,
    }));
    const activeIndex = entries.findIndex(entry => isTaskTimelineActive(entry.window, now));
    const label = now.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    if (activeIndex >= 0) {
      const activeWindow = entries[activeIndex]!.window;
      const span = activeWindow.end.getTime() - activeWindow.start.getTime();
      return {
        label,
        taskId: entries[activeIndex]!.task.id,
        progress: span > 0 ? (now.getTime() - activeWindow.start.getTime()) / span : 0,
        separatorIndex: null,
      };
    }

    const nextIndex = entries.findIndex(entry => entry.window.start > now);
    return {
      label,
      taskId: null,
      progress: 0,
      separatorIndex: nextIndex >= 0 ? nextIndex : entries.length,
    };
  }, [date, now, timelineGroups.scheduled]);

  const visibleSelectedTaskIds = useMemo(
    () =>
      selectedTasks
        .filter(task => !(sortMode === "status" && selectedListGroups.collapsed.has(deriveTaskLifecycle(task))))
        .map(task => task.id),
    [selectedListGroups.collapsed, selectedTasks, sortMode],
  );

  function setOptimisticStatus(
    id: number,
    status: "pending" | "done" | "skipped",
    completedAt: Date | null,
  ) {
    const prev = utils.day.list.getData({ date });
    utils.day.list.setData({ date }, old => old?.map(t => {
      if (t.id !== id) return t;
      if (status === "done" && completedAt) {
        return { ...t, ...advanceTaskExecution(t, completedAt) };
      }
      return { ...t, status };
    }));
    return prev;
  }

  const markDone = trpc.tasks.markDone.useMutation({
    onMutate: async ({ id }) => {
      await utils.day.list.cancel({ date });
      return { prev: setOptimisticStatus(id, "done", new Date()) };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) utils.day.list.setData({ date }, ctx.prev);
    },
    onSettled: () => {
      utils.day.list.invalidate();
      utils.day.monthSummary.invalidate();
    },
  });

  const toggleSkipped = trpc.tasks.toggleSkipped.useMutation({
    onMutate: async ({ id }) => {
      await utils.day.list.cancel({ date });
      const prev = utils.day.list.getData({ date });
      utils.day.list.setData({ date }, old => old?.map(task => {
        if (task.id !== id) return task;
        return {
          ...task,
          ...resetTaskStatusTiming(getSecondaryStatusSwipe(task).status),
        };
      }));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) utils.day.list.setData({ date }, ctx.prev);
    },
    onSuccess: () => {
      utils.day.list.invalidate();
      utils.day.monthSummary.invalidate();
    },
  });

  function chooseSelected(id: number, visibleIds: number[], shiftKey = false) {
    setSelected(prev =>
      shiftKey && lastSelected?.scope === "day"
        ? selectRange(prev, visibleIds, lastSelected.id, id)
        : toggleOne(prev, id),
    );
    setLastSelected({ scope: "day", id });
  }

  function startSelectMode(id: number) {
    setSelectMode(true);
    setSelected(prev => new Set(prev).add(id));
    setLastSelected({ scope: "day", id });
  }

  useEffect(() => {
    setSelected(new Set());
    setSelectMode(false);
    setLastSelected(null);
  }, [date]);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(interval);
  }, []);

  function openOrSelectTask(
    task: typeof selectedTasks[number],
    event: React.MouseEvent<HTMLButtonElement>,
  ) {
    if (selectMode) chooseSelected(task.id, visibleSelectedTaskIds, event.shiftKey);
    else setLocation(`/task/${task.id}`);
  }

  function taskWithSwipe(task: typeof selectedTasks[number], content: React.ReactNode) {
    return (
      <TaskSwipeActions
        task={task}
        disabled={selectMode}
        onSwipeRight={() => markDone.mutate({ id: task.id, clientTimestamp: new Date().toISOString() })}
        onSwipeLeft={() => toggleSkipped.mutate({ id: task.id, clientTimestamp: new Date().toISOString() })}
        onLongPress={() => startSelectMode(task.id)}
      >
        {content}
      </TaskSwipeActions>
    );
  }

  return (
    <>
      <section aria-label={`${label} schedule`} className="space-y-3">
        <DayListHeader
          date={date}
          today={todayKey()}
          count={selectedTasks.length}
          sortMode={sortMode}
          onSortChange={onSortChange}
          filterActiveCount={activeTaskFilterCount(taskFilter)}
          onFilterClick={() => setFilterOpen(true)}
        />
        {tasksQuery.isLoading ? (
          <div className="space-y-1.5">
            {[0, 1, 2].map(i => (
              <div key={i} className="h-10 rounded-lg bg-muted/40 animate-pulse" />
            ))}
          </div>
        ) : selectedTasks.length === 0 ? (
          <p className="text-sm text-muted-foreground/80 italic">Nothing on the schedule.</p>
        ) : sortMode === "time" ? (
          <div className="calendar-timeline">
            {timelineGroups.scheduled.length > 0 && (
              <section className="calendar-timeline-group" aria-label="Scheduled tasks">
                <div className="group-header">
                  <span>Scheduled</span>
                  <span>{timelineGroups.scheduled.length}</span>
                </div>
                <ul className="rows">
                  {timelineGroups.scheduled.map((task, index) => {
                    const timelineWindow = getTaskTimelineWindow(task)!;
                    const active = isTaskTimelineActive(timelineWindow, now);
                    const lifecycle = deriveTaskLifecycle(task);
                    const timelineLifecycle = lifecycle === "doing" && getExecutionControlState(task) === "start"
                      ? "pending"
                      : lifecycle;
                    const colors = resolveTaskColors({
                      barColor: task.barColor,
                      bgColor: task.bgColor,
                      listColor: task.listColor ?? DEFAULT_AREA_COLOR,
                      listBgColor: task.listBgColor ?? null,
                    });
                    const time = timelineWindow.start.toLocaleTimeString(undefined, {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    });
                    return (
                      <Fragment key={task.id}>
                        {timelineNow?.separatorIndex === index && (
                          <li className="now-row"><TimelineNowSeparator time={timelineNow.label} /></li>
                        )}
                        <li>
                        <TaskTimelineRow
                          time={time}
                          active={active}
                          completed={lifecycle === "done"}
                          primaryColor={colors.bar}
                          secondaryColor={colors.soft}
                          currentTime={timelineNow?.taskId === task.id ? timelineNow.label : undefined}
                          currentTimeProgress={timelineNow?.taskId === task.id ? timelineNow.progress : undefined}
                          icon={<TaskLifecycleIcon lifecycle={timelineLifecycle} />}
                        >
                          {taskWithSwipe(task,
                            <ComplexTaskItem
                              task={task}
                              active={active}
                              selected={selected.has(task.id)}
                              onClick={event => openOrSelectTask(task, event)}
                            />,
                          )}
                        </TaskTimelineRow>
                        </li>
                      </Fragment>
                    );
                  })}
                  {timelineNow?.separatorIndex === timelineGroups.scheduled.length && (
                    <li className="now-row"><TimelineNowSeparator time={timelineNow.label} /></li>
                  )}
                </ul>
              </section>
            )}
            {timelineGroups.unscheduled.length > 0 && (
              <section className="calendar-timeline-group unscheduled" aria-label="Unscheduled tasks">
                <div className="group-header">
                  <span>Unscheduled</span>
                  <span>{timelineGroups.unscheduled.length}</span>
                </div>
                <ul className="rows">
                  {timelineGroups.unscheduled.map(task => (
                    <li key={task.id}>
                      {taskWithSwipe(task,
                        <ComplexTaskItem
                          task={task}
                          selected={selected.has(task.id)}
                          onClick={event => openOrSelectTask(task, event)}
                        />,
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        ) : sortMode === "status" ? (
          <ul className="calendar-status-list">
            {selectedTasks.map((task, index) => {
              const lifecycle = deriveTaskLifecycle(task);
              const collapsed = selectedListGroups.collapsed.has(lifecycle);
              const timelineWindow = getTaskTimelineWindow(task);
              const active = timelineWindow ? isTaskTimelineActive(timelineWindow, now) : false;
              return (
                <Fragment key={task.id}>
                  {shouldShowStatusDivider(selectedTasks, index) && (
                    <li className="status-divider">
                      <TaskStatusDivider
                        status={lifecycle}
                        count={statusCount(selectedTasks, lifecycle)}
                        collapsed={collapsed}
                        onToggle={() => selectedListGroups.toggle(lifecycle)}
                      />
                    </li>
                  )}
                  {!collapsed && (
                    <li>
                      {taskWithSwipe(task,
                        <ComplexTaskItem
                          task={task}
                          active={active}
                          selected={selected.has(task.id)}
                          onClick={event => openOrSelectTask(task, event)}
                        />,
                      )}
                    </li>
                  )}
                </Fragment>
              );
            })}
          </ul>
        ) : sortMode === "list" ? (
          <div className="calendar-area-groups">
            {areaGroups.map(group => (
              <section key={group.id ?? "inbox"} className="calendar-timeline-group calendar-area-group" aria-label={`${group.name} tasks`}>
                <div className="group-header">
                  <span>{group.name}</span>
                  <span>{group.tasks.length}</span>
                </div>
                <ul className="rows">
                  {group.tasks.map(task => {
                    const timelineWindow = getTaskTimelineWindow(task);
                    const active = timelineWindow ? isTaskTimelineActive(timelineWindow, now) : false;
                    return (
                      <li key={task.id}>
                        {taskWithSwipe(task,
                          <ComplexTaskItem
                            task={task}
                            active={active}
                            selected={selected.has(task.id)}
                            onClick={event => openOrSelectTask(task, event)}
                          />,
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        ) : (
          <ul className="task-list divide-y divide-border/50 border border-border/60 rounded-xl overflow-hidden bg-card">
            {selectedTasks.map(t => {
              return <li key={t.id} className="task">
                <TaskSwipeActions
                  task={t}
                  disabled={selectMode}
                  onSwipeRight={() => markDone.mutate({ id: t.id, clientTimestamp: new Date().toISOString() })}
                  onSwipeLeft={() => toggleSkipped.mutate({ id: t.id, clientTimestamp: new Date().toISOString() })}
                  onLongPress={() => startSelectMode(t.id)}
                >
                  <SimpleTaskItem
                    task={t}
                    selected={selected.has(t.id)}
                    onClick={event => {
                      if (selectMode) chooseSelected(t.id, visibleSelectedTaskIds, event.shiftKey);
                      else setLocation(`/task/${t.id}`);
                    }}
                  />
                </TaskSwipeActions>
              </li>;
            })}
          </ul>
        )}
      </section>

      {!selectMode && (
        <Button
          onClick={() => setCreateSheetOpen(true)}
          size="lg"
          className="fixed bottom-6 right-6 z-30 h-14 w-14 rounded-full shadow-lg gap-0 p-0"
          aria-label={`Add task to ${label}`}
        >
          <Plus className="h-5 w-5" />
        </Button>
      )}
      <TaskFilterSheet
        open={filterOpen}
        onOpenChange={setFilterOpen}
        value={taskFilter}
        onChange={handleFilterChange}
        lists={filterLists}
      />
      <CreateTaskSheet open={createSheetOpen} onOpenChange={setCreateSheetOpen} date={date} />
      {selectMode && (
        <BulkTaskActionBar
          selectedIds={Array.from(selected)}
          onComplete={() => {
            setSelectMode(false);
            setSelected(new Set());
            setLastSelected(null);
          }}
        />
      )}
    </>
  );
}
